import { genericMemo } from "@redotech/react-util/component";
import classNames from "classnames";
import type { ReactNode } from "react";
import { Flex, type FlexProps } from "../../flex";
import { Text } from "../../text";
import { RedoButton, type RedoButtonProps } from "../buttons/redo-button";
import { RedoIcon, type RedoIconProps } from "../icon/redo-icon";
import { RedoTable, RedoTableProps } from "./redo-table";
import * as tableCardStyles from "./redo-table-card.module.css";

// TODO: Pagination would probably be happy to live here. Update borders if/when added
export const RedoTableCard = genericMemo(function RedoTableCard<DATA>({
  className,
  tableProps,
  title,
  TitleIcon,
  subtitle,
  leftButtonProps,
  rightButtonProps,
  shrink,
  children,
}: {
  className?: string;
  /** `RedoTable` body. Omit when `children` supplies the body instead. */
  tableProps?: RedoTableProps<DATA>;
  title: ReactNode;
  TitleIcon?: RedoIconProps["Icon"];
  subtitle?: ReactNode;
  leftButtonProps?: RedoButtonProps;
  rightButtonProps?: RedoButtonProps;
  shrink?: FlexProps["shrink"];
  /** Body when the table is not a RedoTable. Ignored when tableProps is set. */
  children?: ReactNode;
}) {
  return (
    <Flex
      className={classNames(tableCardStyles.cardWrapper, className)}
      data-arbiter="RedoTableCard"
      dir="column"
      gap="none"
      shrink={shrink}
      w="full"
    >
      {typeof title === "string" ? (
        <Flex
          borderBottomLeftRadius="none"
          borderBottomRightRadius="none"
          borderRadius="lg"
          dir="column"
          gap="xs"
          p="3xl"
          w="full"
        >
          <Flex justify="space-between" w="full">
            <Flex align="center" gap="sm">
              {TitleIcon && (
                <RedoIcon color="secondary" Icon={TitleIcon} size={16} />
              )}
              <Text fontSize="md" fontWeight="semibold">
                {title}
              </Text>
            </Flex>
            <Flex gap="xs">
              {leftButtonProps && <RedoButton {...leftButtonProps} />}
              {rightButtonProps && <RedoButton {...rightButtonProps} />}
            </Flex>
          </Flex>
          {subtitle &&
            (typeof subtitle === "string" ? (
              <Text fontSize="xs" fontWeight="regular" textColor="secondary">
                {subtitle}
              </Text>
            ) : (
              subtitle
            ))}
        </Flex>
      ) : (
        title
      )}

      {tableProps ? <RedoTable {...tableProps} /> : children}
    </Flex>
  );
});
